import { StateCreator } from "zustand";
import { VendState } from ".";
import {
  fetchMachines,
  fetchMachine,
  createMachine as createMachineApi,
  editMachine as editMachineApi,
  deleteMachine as deleteMachineApi,
  activateMachine as activateMachineApi,
  deactivateMachine as deactivateMachineApi,
} from "../api/machine/api";
import { Machine } from "../types";
import { getLocationId, machineTypeLabel, withWriteAction } from "../utils";
import { useUserStore } from "../../user/state/store";
import { createLoadCoordinator, runLoad } from "./helpers";
import { getTaskIdentityKey } from "../taskContext";
import { useBillingActionModalStore } from "./billingActionModal";
import { taskScopeKeys } from "../taskScopes";
import { resetMachineCollectionSummaryCache } from "../liveSales/store";

export type MachineSlice = {
  machines: Machine[];
  availableMachines: Machine[];
  machinesLoading: boolean;
  machinesError: string | null;
  machinesHydrated: boolean;
  machinesFetchedAt: number | null;
  setMachines: (machines: Machine[]) => void;
  setAvailableMachines: (machines: Machine[]) => void;
  addMachine: (machine: Machine) => void;
  loadMachines: (options?: { force?: boolean }) => Promise<void>;
  loadAvailableMachines: () => Promise<void>;
  fetchMachine: (id: string) => Promise<Machine>;
  refreshMachinesByIds: (ids: string[]) => Promise<void>;
  createMachine: (machine: Machine) => Promise<Machine>;
  editMachine: (machine: Machine) => Promise<Machine>;
  activateMachine: (machineId: string) => Promise<Machine>;
  deactivateMachine: (machineId: string) => Promise<Machine>;
  deleteMachine: (id: string) => Promise<{ id: string }>;
  assignMachineToLocation: (
    machineId: string,
    locationId: string
  ) => Promise<Machine>;
  unassignMachine: (machineId: string) => Promise<Machine>;
};

export const deriveAvailable = (machines: Machine[]) =>
  machines.filter((m) => !getLocationId(m.location as any));

const machinesLoads = createLoadCoordinator(() => `${useUserStore.getState().id || ""}:${getTaskIdentityKey()}`);
export const resetMachineLoadState = machinesLoads.reset;

const normalizeMachines = (data: Machine[]) =>
  data.map((m) => ({
    ...m,
    type: machineTypeLabel(m.type),
  }));

const replaceMachine = (machines: Machine[], updated: Machine) => {
  const normalized = normalizeMachines([updated])[0];
  if (!normalized.id) return normalizeMachines(machines);
  let found = false;
  const next = machines.map((machine) => {
    if (machine.id !== normalized.id) return machine;
    found = true;
    return normalized;
  });
  return found ? next : [...next, normalized];
};

const refreshTasksAndRoutePlanner = async (
  state: VendState,
  machineId?: string | null
) => {
  if (machineId) {
    await state.loadTasks?.({
      force: true,
      silent: true,
      filters: { machineId },
      scopeKey: taskScopeKeys.machine(machineId),
    });
    return;
  }
  await state.loadTasks?.({ force: true });
};

const showActivationChargeFailed = () => {
  window.setTimeout(() => {
    useBillingActionModalStore.getState().showActivationFailure();
  }, 0);
};

export const createMachineSlice: StateCreator<
  VendState,
  [],
  [],
  MachineSlice
> = (set, get) => ({
  machines: [],
  availableMachines: [],
  machinesLoading: false,
  machinesError: null,
  machinesHydrated: false,
  machinesFetchedAt: null,
  setMachines: (machines) => {
    machinesLoads.invalidate();
    set({ machines, availableMachines: deriveAvailable(machines) } as VendState);
  },
  setAvailableMachines: (availableMachines) =>
    set({ availableMachines } as VendState),
  addMachine: (machine) => {
    machinesLoads.invalidate();
    const state = get();
    const machines = replaceMachine(state.machines, machine);
    set({
      machines,
      availableMachines: deriveAvailable(machines),
    } as VendState);
  },
  loadMachines: async (options) => {
    if (!options?.force && get().machinesHydrated && !get().machinesError) return;
    return machinesLoads.run(async (current) => {
      const contextCurrent = machinesLoads.capture();
      await runLoad({
        setLoading: (loading) => { if (contextCurrent()) set({ machinesLoading: loading } as VendState); },
        fetcher: fetchMachines,
        setError: (message) => { if (current()) set({ machinesError: message } as VendState); },
        onSuccess: (data) => {
          if (!current()) return;
          const machines = normalizeMachines(data);
          set({ machines, availableMachines: deriveAvailable(machines), machinesFetchedAt: Date.now() } as VendState);
        },
      });
      if (current()) set({ machinesHydrated: true } as VendState);
    }, Boolean(options?.force));
  },
  loadAvailableMachines: async () => {
    const state = get();
    if ((state.machines || []).length === 0) {
      await state.loadMachines();
      return;
    }
    set({ availableMachines: deriveAvailable(state.machines) } as VendState);
  },
  fetchMachine: async (id: string) => {
    return fetchMachine(id);
  },
  refreshMachinesByIds: async (ids) => {
    const uniqueIds = Array.from(new Set(ids.filter(Boolean)));
    if (!uniqueIds.length) return;
    const contextCurrent = machinesLoads.capture();
    const snapshotCurrent = machinesLoads.capture(true);
    const results = await Promise.allSettled(
      uniqueIds.map((id) => fetchMachine(id))
    );
    if (!contextCurrent()) return;
    if (!snapshotCurrent()) {
      get().loadMachines({ force: true }).catch(() => {});
      return;
    }
    const refreshed = results.flatMap((result) =>
      result.status === "fulfilled" ? [result.value] : []
    );
    if (!refreshed.length) {
      const failure = results.find(
        (result): result is PromiseRejectedResult => result.status === "rejected"
      );
      throw failure?.reason || new Error("Failed to refresh machines");
    }
    machinesLoads.invalidate();
    set((current) => {
      let machines = current.machines;
      refreshed.forEach((machine) => {
        machines = replaceMachine(machines, machine);
      });
      return {
        machines,
        availableMachines: deriveAvailable(machines),
      } as VendState;
    });
  },
  createMachine: async (machine) => {
    return await withWriteAction(
      "Creating Machine",
      async () => {
      const contextCurrent = machinesLoads.capture();
      const created = await createMachineApi(machine);
      if (!contextCurrent()) return created;
      machinesLoads.invalidate();
      if (created.id) resetMachineCollectionSummaryCache([created.id]);
      const machines = replaceMachine((get() as VendState).machines, created);
      set({
        machines,
        availableMachines: deriveAvailable(machines),
      } as VendState);
      if (getLocationId(created.location as any)) {
        get().loadLocations?.({ force: true }).catch(() => {});
      }
      if (getLocationId(created.location as any)) {
        refreshTasksAndRoutePlanner(get() as VendState, created.id).catch(() => {});
      }
      useUserStore.getState().refreshSubscription({ force: true }).catch(() => {});
      if ((created as any).activationChargeFailed) {
        showActivationChargeFailed();
      }
      return created;
      }
    );
  },
  editMachine: async (machine) => {
    return await withWriteAction("Saving Machine", async () => {
      const state = get() as VendState;
      const current = state.machines.find((m) => m.id === machine.id);
      const previousLocationId = getLocationId(current?.location as any);
      const contextCurrent = machinesLoads.capture();
      const updated = await editMachineApi(machine);
      if (!contextCurrent()) return updated;
      machinesLoads.invalidate();
      if (updated.id) resetMachineCollectionSummaryCache([updated.id]);
      const nextLocationId = getLocationId(updated.location as any);
      const machines = replaceMachine((get() as VendState).machines, updated);
      set({
        machines,
        availableMachines: deriveAvailable(machines),
      } as VendState);
      if (
        previousLocationId !== nextLocationId &&
        typeof get().loadLocations === "function"
      ) {
        get().loadLocations({ force: true }).catch(() => {});
      }
      refreshTasksAndRoutePlanner(get() as VendState, updated.id).catch(() => {});
      return updated;
    });
  },
  activateMachine: async (machineId) => {
    return await withWriteAction("Activating Machine", async () => {
      const contextCurrent = machinesLoads.capture();
      const updated = await activateMachineApi(machineId);
      if (!contextCurrent()) return updated;
      machinesLoads.invalidate();
      resetMachineCollectionSummaryCache(updated.id ? [updated.id] : [machineId]);
      const machines = replaceMachine((get() as VendState).machines, updated);
      set({
        machines,
        availableMachines: deriveAvailable(machines),
      } as VendState);
      await refreshTasksAndRoutePlanner(get() as VendState, updated.id);
      useUserStore.getState().refreshSubscription({ force: true }).catch(() => {});
      if ((updated as any).activationChargeFailed) {
        showActivationChargeFailed();
      }
      return updated;
    });
  },
  deactivateMachine: async (machineId) => {
    return await withWriteAction("Deactivating Machine", async () => {
      const contextCurrent = machinesLoads.capture();
      const updated = await deactivateMachineApi(machineId);
      if (!contextCurrent()) return updated;
      machinesLoads.invalidate();
      resetMachineCollectionSummaryCache(updated.id ? [updated.id] : [machineId]);
      const machines = replaceMachine((get() as VendState).machines, updated);
      set({
        machines,
        availableMachines: deriveAvailable(machines),
      } as VendState);
      refreshTasksAndRoutePlanner(get() as VendState, updated.id).catch(() => {});
      useUserStore.getState().refreshSubscription({ force: true }).catch(() => {});
      return updated;
    });
  },
  deleteMachine: async (id: string) => {
    return await withWriteAction("Deleting Machine", async () => {
      const contextCurrent = machinesLoads.capture();
      const result = await deleteMachineApi(id);
      if (!contextCurrent()) return result;
      machinesLoads.invalidate();
      resetMachineCollectionSummaryCache([id]);
      const machines = get().machines.filter((machine) => machine.id !== id);
      set({
        machines,
        availableMachines: deriveAvailable(machines),
      } as VendState);
      get().loadLocations?.({ force: true }).catch(() => {});
      refreshTasksAndRoutePlanner(get() as VendState).catch(() => {});
      useUserStore.getState().refreshSubscription({ force: true }).catch(() => {});
      return result;
    });
  },
  assignMachineToLocation: async (machineId, locationId) => {
    return await withWriteAction("Assigning Machine", async () => {
      const state = get();
      const machine = state.machines.find((m) => m.id === machineId);
      if (!machine) throw new Error("Machine not found");
      const contextCurrent = machinesLoads.capture();
      const updated = await editMachineApi({
        ...machine,
        location: locationId,
        assigned: true,
      });
      if (!contextCurrent()) return updated;
      machinesLoads.invalidate();
      resetMachineCollectionSummaryCache([machineId]);
      const machines = replaceMachine((get() as VendState).machines, updated);
      set({
        machines,
        availableMachines: deriveAvailable(machines),
      } as VendState);
      get().loadLocations?.({ force: true }).catch(() => {});
      refreshTasksAndRoutePlanner(get() as VendState, updated.id).catch(() => {});
      return updated;
    });
  },
  unassignMachine: async (machineId) => {
    return await withWriteAction("Unassigning Machine", async () => {
      const state = get();
      const machine = state.machines.find((m) => m.id === machineId);
      if (!machine) throw new Error("Machine not found");
      const contextCurrent = machinesLoads.capture();
      const updated = await editMachineApi({
        ...machine,
        location: null,
        assigned: false,
      });
      if (!contextCurrent()) return updated;
      machinesLoads.invalidate();
      resetMachineCollectionSummaryCache([machineId]);
      const machines = replaceMachine((get() as VendState).machines, updated);
      set({
        machines,
        availableMachines: deriveAvailable(machines),
      } as VendState);
      get().loadLocations?.({ force: true }).catch(() => {});
      refreshTasksAndRoutePlanner(get() as VendState, updated.id).catch(() => {});
      return updated;
    });
  },
});
